import { Button } from '@/components/ui/button';
import { useEffect } from 'react';
import { usePreference } from '@/hooks/useUserPreferences';
import { useAskCannafax } from '@/contexts/AskCannafaxContext';
import { cn } from '@/lib/utils';

/**
 * One-time welcome glow on the Ask Cannafax header button.
 *
 * Shown once per user (cross-device via user_preferences) until they open
 * the panel or click the button — then it never returns. Part of the
 * "Your Desk" first-session welcome: a gentle pointer, never a tour.
 */
export function AskCannafaxGlow({ children, hold = false }: { children: React.ReactNode; hold?: boolean }) {
  const [seen, setSeen] = usePreference<boolean>('ask-cannafax-glow-seen', false);
  const { isOpen } = useAskCannafax();

  // Opening the panel (by any means) counts as having found it.
  useEffect(() => {
    if (isOpen && !seen) setSeen(true);
  }, [isOpen, seen, setSeen]);

  // `hold` defers (never consumes) the welcome while another "start here" is on screen.
  if (seen || hold) return <>{children}</>;

  return (
    <div className="relative">
      {/* Soft pulsing ring */}
      <span
        aria-hidden
        className="absolute inset-0 rounded-full bg-amber-400/40 animate-ping [animation-duration:2.2s]"
      />
      {children}
      {/* One-line invitation bubble */}
      <Button variant="quiet" size="content"
        type="button"
        onClick={() => setSeen(true)}
        className={cn("font-normal justify-start", cn(
          // Hangs just below the header button, right-aligned — never over the
          // top-bar links or the left-aligned page title.
          'absolute right-0 top-full z-50 mt-3 hidden w-max whitespace-nowrap rounded-xl border border-amber-200/60 md:block',
          'bg-background px-3 py-2 text-left shadow-lg shadow-amber-500/10',
          'text-xs text-muted-foreground transition-colors hover:text-foreground',
        ))}
      >
        <span className="font-medium text-foreground">Ask me anything</span>
        {' '}— I know your desk.
      </Button>
    </div>
  );
}
